import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import cx from 'classnames';
import { tabBarClass } from '@app/constants/styles';

const ACTIVE_TAB = '[aria-current="page"], [aria-selected="true"], [aria-pressed="true"]';

/** Over each edge that has tabs past it; `-4` on a phone, where the row runs to the screen's edge. */
// The bottom padding matches a tab's pb-3 and underline, so the chevron centres on the label, not the row.
const arrowClass =
  'absolute top-0 bottom-px z-10 flex w-10 cursor-pointer items-center pb-[13px] text-foreground to-transparent transition-opacity duration-200';

/**
 * An underline tab bar that scrolls sideways on a narrow screen and keeps the active tab in view. Where
 * tabs are cut off, a fade and an arrow say so, and the arrow scrolls the next ones in.
 */
export const TabBar = ({
  children,
  className,
  wrapperClassName,
  fadeClassName = 'from-background',
  activeKey,
}: {
  children: React.ReactNode;
  /** The scrolling row itself: its border. */
  className?: string;
  /** Around the row and its arrows: margins go here, so the arrows stay the row's height. */
  wrapperClassName?: string;
  /** The `from-*` surface the fades resolve to: whatever the bar sits on. */
  fadeClassName?: string;
  /** Changes when the active tab does, so the bar can bring it into view. */
  activeKey?: string;
}): React.ReactElement => {
  const ref = useRef<HTMLDivElement>(null);
  // Hidden until measured: a bar that fits never shows either arrow.
  const [edges, setEdges] = useState({ before: false, after: false });

  const measure = useCallback(() => {
    const bar = ref.current;
    if (!bar) return;
    const before = bar.scrollLeft > 2;
    const after = bar.scrollWidth - bar.clientWidth - bar.scrollLeft > 2;
    setEdges(prev => (prev.before === before && prev.after === after ? prev : { before, after }));
  }, []);

  useLayoutEffect(() => {
    const bar = ref.current;
    const active = bar?.querySelector<HTMLElement>(ACTIVE_TAB);
    if (bar && active && bar.scrollWidth > bar.clientWidth) {
      bar.scrollLeft = active.offsetLeft - (bar.clientWidth - active.offsetWidth) / 2;
    }
    measure();
  }, [activeKey, measure]);

  useEffect(() => {
    const bar = ref.current;
    if (!bar || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    Array.from(bar.children).forEach(child => observer.observe(child));
    return () => observer.disconnect();
  }, [measure, children]);

  // Most of the row's width, so the tab cut off at the edge comes fully into view.
  const step = (direction: 1 | -1): void => {
    const bar = ref.current;
    bar?.scrollBy({ left: direction * bar.clientWidth * 0.6, behavior: 'smooth' });
  };

  // Out of the tab order and hidden from readers: every tab is reachable without them.
  return (
    <div className={cx('relative', wrapperClassName)}>
      <div ref={ref} onScroll={measure} className={cx(tabBarClass, className)}>
        {children}
      </div>
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={() => step(-1)}
        className={cx(
          arrowClass,
          'left-0 justify-start bg-gradient-to-r from-55% pl-0.5 max-md:-left-4 max-md:pl-2',
          fadeClassName,
          !edges.before && 'pointer-events-none opacity-0',
        )}
      >
        <ChevronLeft className="size-4" />
      </button>
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={() => step(1)}
        className={cx(
          arrowClass,
          'right-0 justify-end bg-gradient-to-l from-55% pr-0.5 max-md:-right-4 max-md:pr-2',
          fadeClassName,
          !edges.after && 'pointer-events-none opacity-0',
        )}
      >
        <ChevronRight className="size-4" />
      </button>
    </div>
  );
};
